/** The six steps, per 10.03.04.03 AC 1. Values double as the step index. */
export enum ApplicationStep {
  PersonalData = 0,
  Location = 1,
  Degree = 2,
  Subjects = 3,
  Message = 4,
  Attachments = 5,
}

export const STEP_COUNT = 6;

export interface PersonalData {
  salutation: string;
  firstname: string;
  lastname: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  email: string;
  phone: string;
  mobilePhone: string;
}

export interface SubjectEntry {
  subject: string;
  level: string;
}

export interface ApplicationState {
  step: ApplicationStep;
  personalData: PersonalData;
  /** Up to four Standort slugs; index 0 is the desired one. */
  locationSlugs: (string | null)[];
  /**
   * Labels the applicant saw for `locationSlugs`, index-aligned with it. Kept
   * so step 2's inputs still show the chosen Standort after the step unmounts
   * and remounts; it is display state only and never submitted.
   */
  locationLabels: string[];
  /**
   * `centerNumber` per slot, index-aligned with `locationSlugs`. Submitted as
   * `schoolNumbers`: the backend no longer resolves slugs, so this is the
   * value that reaches SH.
   */
  locationCenterNumbers: (number | null)[];
  /**
   * Slot 0 is the page's own Standort (the Standort-Jobs Page, 10.02.08) and
   * is shown as fixed text instead of a picker; only the three alternatives
   * stay editable.
   */
  locationFixed: boolean;
  interestedInOnline: boolean;
  degree: string;
  /** Up to three subject rows; the first is required. */
  subjects: SubjectEntry[];
  message: string;
  /** Up to five attachments; `null` for an empty slot. */
  files: (File | null)[];
  submitted: boolean;
  submitting: boolean;
}

export type ApplicationAction =
  | { type: 'STEP_NEXT' }
  | { type: 'STEP_BACK' }
  /** Jump straight to a completed step, from the step indicator. */
  | { type: 'STEP_SET'; step: ApplicationStep }
  | { type: 'UPDATE_PERSONAL'; values: Partial<PersonalData> }
  | {
      type: 'UPDATE_LOCATIONS';
      slugs: (string | null)[];
      labels: string[];
      centerNumbers: (number | null)[];
    }
  | { type: 'UPDATE_ONLINE_INTEREST'; interested: boolean }
  | { type: 'UPDATE_DEGREE'; degree: string }
  | { type: 'UPDATE_SUBJECTS'; subjects: SubjectEntry[] }
  | { type: 'UPDATE_MESSAGE'; message: string }
  | { type: 'SET_FILES'; files: (File | null)[] }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS' };

/** The page's own Standort, pre-selected as the desired one (slot 0). */
export interface PresetLocation {
  slug: string;
  label: string;
  centerNumber: number;
}

export const initialApplicationState = (
  preset?: PresetLocation,
): ApplicationState => ({
  step: ApplicationStep.PersonalData,
  personalData: {
    salutation: '',
    firstname: '',
    lastname: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    email: '',
    phone: '',
    mobilePhone: '',
  },
  locationSlugs: [preset?.slug ?? null, null, null, null],
  locationLabels: [preset?.label ?? '', '', '', ''],
  locationCenterNumbers: [preset?.centerNumber ?? null, null, null, null],
  locationFixed: preset !== undefined,
  interestedInOnline: false,
  degree: '',
  subjects: [{ subject: '', level: '' }],
  message: '',
  files: [null, null, null, null, null],
  submitted: false,
  submitting: false,
});

const clampStep = (step: number): ApplicationStep =>
  Math.min(
    Math.max(step, ApplicationStep.PersonalData),
    ApplicationStep.Attachments,
  );

export const applicationReducer = (
  state: ApplicationState,
  action: ApplicationAction,
): ApplicationState => {
  switch (action.type) {
    case 'STEP_NEXT':
      return { ...state, step: clampStep(state.step + 1) };

    case 'STEP_BACK':
      return { ...state, step: clampStep(state.step - 1) };

    case 'STEP_SET':
      // Progression is linear: the indicator may only send the user backwards,
      // never skip ahead over an unvalidated step (AC 1).
      return action.step < state.step
        ? { ...state, step: clampStep(action.step) }
        : state;

    case 'UPDATE_PERSONAL':
      return {
        ...state,
        personalData: { ...state.personalData, ...action.values },
      };

    case 'UPDATE_LOCATIONS':
      return {
        ...state,
        locationSlugs: action.slugs,
        locationLabels: action.labels,
        locationCenterNumbers: action.centerNumbers,
      };

    case 'UPDATE_ONLINE_INTEREST':
      return { ...state, interestedInOnline: action.interested };

    case 'UPDATE_DEGREE':
      return { ...state, degree: action.degree };

    case 'UPDATE_SUBJECTS':
      return { ...state, subjects: action.subjects };

    case 'UPDATE_MESSAGE':
      return { ...state, message: action.message };

    case 'SET_FILES':
      return { ...state, files: action.files };

    case 'SUBMIT_START':
      return { ...state, submitting: true };

    case 'SUBMIT_SUCCESS':
      // The applicant always reaches the confirmation, whether or not SH
      // was reachable — the backend enqueues and reports success regardless
      // (AC 8).
      return { ...state, submitting: false, submitted: true };

    default:
      return state;
  }
};

/** Standort slugs actually chosen, in order, with empty slots removed. */
export const selectedLocationSlugs = (state: ApplicationState): string[] =>
  state.locationSlugs.filter((slug): slug is string => Boolean(slug));

/**
 * The `schoolNumbers` payload, kept in the same order as
 * {@link selectedLocationSlugs} — index 0 is the desired Standort. Only slots
 * that carry both a slug and a number contribute, so the two arrays always
 * line up.
 */
export const selectedCenterNumbers = (state: ApplicationState): number[] =>
  state.locationSlugs
    .map((slug, index) => (slug ? state.locationCenterNumbers[index] : null))
    .filter((n): n is number => typeof n === 'number');

/** Subject rows the applicant filled in completely. */
export const completedSubjects = (state: ApplicationState): SubjectEntry[] =>
  state.subjects.filter((entry) => entry.subject && entry.level);

/** Attachments actually chosen. At least one is required by AC 7. */
export const selectedFiles = (state: ApplicationState): File[] =>
  state.files.filter((file): file is File => file !== null);
